import React, { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { productService } from '../../services/productService';
import { Product } from '../../types/product';

interface ProductPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (productIds: number[]) => Promise<void> | void;
  title?: string;
}

const PAGE_SIZE = 50;
const SELECT_ALL_MAX_PAGES = 40; // safety cap (~2000 products) when "select all filtered"

/**
 * Modal de consulta de produtos por nome e SKU. Permite marcar produtos
 * individualmente ou "selecionar todos os produtos da lista filtrada"
 * (busca todas as páginas correspondentes ao termo) para associá-los em lote.
 */
export const ProductPickerModal: React.FC<ProductPickerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Buscar produtos',
}) => {
  const [query, setQuery] = useState('');
  const [lastQuery, setLastQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setQuery('');
    setLastQuery('');
    setResults([]);
    setSelected(new Set());
    setPage(1);
    setHasMore(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runSearch = async (term: string, pageToLoad: number, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const { products } = await productService.searchProducts(term.trim(), pageToLoad, PAGE_SIZE, {
        is_active: true,
      });
      setResults((prev) => (append ? [...prev, ...products] : products));
      setHasMore(products.length === PAGE_SIZE);
      setPage(pageToLoad);
      setLastQuery(term.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao buscar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    runSearch(query, 1, false);
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allLoadedSelected = results.length > 0 && results.every((p) => selected.has(p.id));

  // Selects EVERY product matching the current filter (across all pages), not just
  // the ones currently loaded. Fetches sequentially until exhausted (capped).
  const handleSelectAllFiltered = async () => {
    if (allLoadedSelected && !hasMore) {
      // Toggle off: clear selection of loaded items.
      setSelected((prev) => {
        const next = new Set(prev);
        results.forEach((p) => next.delete(p.id));
        return next;
      });
      return;
    }
    setSelectingAll(true);
    setError(null);
    try {
      const term = lastQuery || query.trim();
      const ids = new Set(selected);
      const collected: Product[] = [];
      let p = 1;
      // eslint-disable-next-line no-constant-condition
      while (p <= SELECT_ALL_MAX_PAGES) {
        const { products } = await productService.searchProducts(term, p, PAGE_SIZE, { is_active: true });
        products.forEach((prod) => {
          ids.add(prod.id);
          collected.push(prod);
        });
        if (products.length < PAGE_SIZE) break;
        p += 1;
      }
      // Merge collected into the visible list (dedupe by id).
      setResults((prev) => {
        const map = new Map<number, Product>();
        [...prev, ...collected].forEach((prod) => map.set(prod.id, prod));
        return Array.from(map.values());
      });
      setHasMore(false);
      setSelected(ids);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao selecionar todos os produtos');
    } finally {
      setSelectingAll(false);
    }
  };

  const handleConfirm = async () => {
    if (selected.size === 0) return;
    setConfirming(true);
    try {
      await onConfirm(Array.from(selected));
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao associar produtos');
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-h3">{title}</h2>
      </div>

      <div className="px-6 py-4">
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou SKU (ex.: caixa gaveta)"
              className="input-field w-full pl-9 pr-3 py-2 text-sm"
              autoFocus
            />
          </div>
          <Button type="submit" variant="secondary" loading={loading} disabled={!query.trim()}>
            Buscar
          </Button>
        </form>

        {error && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        {results.length > 0 && (
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={allLoadedSelected && !hasMore}
                onChange={handleSelectAllFiltered}
                disabled={selectingAll}
                className="rounded border-slate-300"
              />
              {selectingAll ? (
                <span className="flex items-center gap-1">
                  <Loader2 size={14} className="animate-spin" /> Selecionando todos…
                </span>
              ) : (
                <span>Selecionar todos os produtos desta lista filtrada</span>
              )}
            </label>
            <span className="text-sm text-slate-500">{selected.size} selecionado(s)</span>
          </div>
        )}

        <div className="max-h-80 overflow-y-auto custom-scrollbar border border-slate-200 rounded-lg">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400">
              {lastQuery ? 'Nenhum produto encontrado.' : 'Digite um termo e clique em Buscar.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="w-10 px-3 py-2"></th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">SKU</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Descrição</th>
                </tr>
              </thead>
              <tbody>
                {results.map((p) => (
                  <tr
                    key={p.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleOne(p.id)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggleOne(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-slate-300"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{p.sku}</td>
                    <td className="px-3 py-2 text-slate-700">{p.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {hasMore && !selectingAll && (
          <div className="mt-2 text-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={loading}
              onClick={() => runSearch(lastQuery, page + 1, true)}
            >
              Carregar mais
            </Button>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-3">
        <Button variant="outline" onClick={handleClose} disabled={confirming}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          loading={confirming}
          disabled={selected.size === 0}
        >
          Associar {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </div>
    </Modal>
  );
};

export default ProductPickerModal;
